import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  Lock,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
} from "lucide-react";

import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import { useToast } from "@/hooks/use-toast.js";
import authAPI from "@/lib/api/auth";
import routes from "@/routes/routes";

// Password reset confirmation validation schema
const resetConfirmSchema = z.object({
  password: z.string().min(1),
});

const PasswordResetConfirmContext = createContext(null);

function PasswordResetConfirmProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const { key } = useParams();

  const [isKeyValid, setKeyValid] = useState(false);
  const [loading, setLoading] = useState(true);

  const verifyKey = async (key) => {
    const response = await authAPI.verifyResetKey(key);
    if (response.ok) {
      setKeyValid(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (key) {
      verifyKey(key);
    } else {
      navigate(routes.account, { replace: true });
    }
  }, [key]);

  const { toast } = useToast();

  const resetConfirmForm = useForm({
    resolver: zodResolver(resetConfirmSchema),
    defaultValues: {
      password: "",
    },
  });

  const onSubmit = async ({ password }) => {
    setIsLoading(true);
    setError("");

    const response = await authAPI.resetPassword(key, password);

    if (!response.ok && response.status !== 401) {
      const jsonErrors = await response.json();
      setError(
        jsonErrors ||
          "Une erreur est survenue lors de la réinitialisation du mot de passe",
      );
      setIsLoading(false);
      return;
    }

    setIsSuccess(true);
    toast({
      title: "Mot de passe réinitialisé",
      description:
        "Votre mot de passe a été modifié avec succès et vous êtes maintenant connecté.",
    });
    setIsLoading(false);
  };

  const handleBackToLogin = () => {
    navigate("/login", { replace: true });
  };

  const value = {
    error,
    setError,
    isLoading,
    setIsLoading,
    isSuccess,
    setIsSuccess,
    showPassword,
    setShowPassword,
    resetConfirmForm,
    onSubmit,
    handleBackToLogin,
    key,
    isKeyValid,
    loading,
  };

  return (
    <PasswordResetConfirmContext.Provider value={value}>
      {children}
    </PasswordResetConfirmContext.Provider>
  );
}

function usePasswordResetConfirm() {
  return useContext(PasswordResetConfirmContext);
}

function PasswordResetConfirmComponent() {
  const { isSuccess, isKeyValid, loading, handleBackToLogin } =
    usePasswordResetConfirm();

  if (isSuccess) return <PasswordResetConfirmSuccess />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Vérification du lien...
          </p>
        </div>
      </div>
    );
  }

  if (!isKeyValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-destructive/50">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Lien invalide</CardTitle>
            <CardDescription>
              Ce lien de réinitialisation est expiré ou incorrect.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={handleBackToLogin}>
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <PasswordResetConfirmHeader />
        </CardHeader>

        <CardContent className="space-y-6">
          <PasswordResetConfirmForm />
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordResetConfirmHeader() {
  return (
    <Fragment>
      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
        <Lock className="h-6 w-6 text-primary" />
      </div>
      <CardTitle className="text-2xl font-bold">Nouveau mot de passe</CardTitle>
      <CardDescription>Entrez votre nouveau mot de passe</CardDescription>
    </Fragment>
  );
}

function PasswordResetConfirmForm() {
  const {
    error,
    isLoading,
    showPassword,
    setShowPassword,
    resetConfirmForm,
    onSubmit,
    handleBackToLogin,
  } = usePasswordResetConfirm();

  return (
    <Fragment>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={resetConfirmForm.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <div className="space-y-2">
          <Label htmlFor="password">Nouveau mot de passe</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="w-full pr-10"
              {...resetConfirmForm.register("password")}
              disabled={isLoading}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          {resetConfirmForm.formState.errors.password && (
            <p className="text-sm text-destructive">
              {resetConfirmForm.formState.errors.password.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Réinitialisation en cours...
            </>
          ) : (
            "Confirmer"
          )}
        </Button>
      </form>

      <div className="space-y-4">
        <div className="text-center">
          <Button
            variant="ghost"
            className="text-sm text-muted-foreground hover:text-primary"
            onClick={handleBackToLogin}
            disabled={isLoading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la connexion
          </Button>
        </div>
      </div>
    </Fragment>
  );
}

function PasswordResetConfirmSuccess() {
  const { handleBackToLogin } = usePasswordResetConfirm();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-800">
            Mot de passe modifié
          </CardTitle>
          <CardDescription className="text-green-700">
            Votre mot de passe a été réinitialisé avec succès
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <Button
              onClick={handleBackToLogin}
              variant="outline"
              className="w-full"
            >
              Connectez-vous
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PasswordResetConfirm() {
  return (
    <PasswordResetConfirmProvider>
      <PasswordResetConfirmComponent />
    </PasswordResetConfirmProvider>
  );
}
